/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useAuth } from '../modules/auth'
import { filterOtherContact } from '../services/contacts'
import {
  readChecklistAssignees,
  writeChecklistAssignees,
} from '../components/TextEditor'

interface ChecklistAssignee {
  id: string
  name: string
  source: 'board' | 'contact' | 'freetext'
  email?: string
  hasLicense?: boolean
}

interface Options {
  /** Read the live Quill editor; returned element holds the checklist item. */
  getEditorRoot: () => HTMLElement | null
  /** Parent form state setter — keeps Quill HTML in sync after the DOM edit. */
  setEditor: (html: string) => void
  /** Fire assignment alerts for the newly-upgraded contact. */
  sendAlerts: (item: HTMLElement, added: ChecklistAssignee[]) => void
}

/**
 * After the "+ Add <name> to contacts" flow saves a contact, replace the
 * freetext assignee previously stamped on the checklist item with a proper
 * `source: 'contact'` entry (id + email). Also fires the assignment alert
 * for the upgraded contact so the email goes out via the backend's
 * license-table resolution path.
 */
export function useUpgradeFreeTextAssignee({
  getEditorRoot,
  setEditor,
  sendAlerts,
}: Options) {
  const { currentUser } = useAuth()

  return async (itemId: string, name: string): Promise<void> => {
    if (!itemId || !name) return
    const root = getEditorRoot()
    if (!root) return
    const item = root.querySelector(
      `[data-checklist-item-id="${itemId}"]`
    ) as HTMLElement | null
    if (!item) return
    const userId: string | undefined = currentUser?.data?.user?.id
    const companyId: string | undefined =
      currentUser?.data?.user?.default_organization_id ||
      currentUser?.data?.user?.organizationId ||
      undefined
    if (!userId) return
    try {
      const res: any = await filterOtherContact(name, userId, companyId ?? '')
      const list = res?.data?.data ?? res?.data ?? []
      const needle = name.trim().toLowerCase()
      const match = Array.isArray(list)
        ? list.find(
            (c: any) => (c?.fullName ?? '').trim().toLowerCase() === needle
          )
        : null
      if (!match?.id) return
      const existing = readChecklistAssignees(item)
      const freetextIdx = existing.findIndex(
        (a) =>
          a.source === 'freetext' && a.name.trim().toLowerCase() === needle
      )
      if (freetextIdx === -1) return
      const upgraded: ChecklistAssignee = {
        id: match.id,
        name: match.fullName ?? name,
        source: 'contact',
        email: match.email,
        hasLicense: !!match.hasLicense,
      }
      const next = existing.slice()
      next[freetextIdx] = upgraded
      writeChecklistAssignees(item, next)
      setEditor(root.innerHTML)
      sendAlerts(item, [upgraded])
    } catch {
      // swallow — contact save already succeeded; silent failure is fine
    }
  }
}
